<script lang="ts" setup>

  import type { GolfCard } from '~/core/types/golf-card.type'

  import { BngHoles } from '../../holes'
  import { BngCardTopInfos } from '../card-top-infos'

  type Props = {
    golf: GolfCard
    isFavorite?: boolean
    rating?: boolean
    departures?: boolean
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'toggleFavorite'): void
  }>()

  const { rate, services } = useGolfCard(props.golf)
</script>
<template>
  <NuxtLink
    :to="`/golf/${props.golf.slug}`"
    class="c-home-card card"
  >
    <bng-card-top-infos
      v-if="props.departures"
      :main="props.golf.departure_count + ' départs disponibles'"
    />
    <div
      class="picture"
      :style="{ 'background-image': `url(${props.golf.thumb})` }"
    >
      <button
        class="favorite"
        @click.prevent="() => emit('toggleFavorite')"
      >
        <SvgIcon
          :class="{ active: props.isFavorite }"
          icon="favorite"
          :width="18"
          :height="16"
          :desktop-width="18"
          :desktop-height="16"
        />
      </button>
    </div>
    <div class="content">
      <div class="golf-details">
        <div class="name-promotion">
          <div class="name">
            <span>{{ props.golf.name }}</span>
          </div>
          <SvgIcon
            icon="promotion"
            :width="20"
            :height="20"
            :desktop-width="24"
            :desktop-height="24"
          />
        </div>
        <div class="location">
          <span>{{ props.golf.commune }}</span>
        </div>
        <div
          v-if="rating"
          class="rating"
        >
          <template v-if="props.golf.rating">
            <SvgIcon
              icon="star"
              :width="22"
              :height="22"
              :desktop-width="22"
              :desktop-height="22"
            />
            <span class="note">
              <strong>{{ props.golf.rating }}</strong>/5
            </span>
            <span class="rates-count"> ({{ rate }} notes) </span>
          </template>
          <template v-else>
            <span class="no-rate">Aucun avis</span>
          </template>
        </div>
      </div>
      <div class="bottom">
        <div class="golf-infos">
          <BngHoles
            small
            :model-value="props.golf.holes ?? []"
          />
          <div class="services">
            <span>{{ services }}</span>
          </div>
        </div>
        <div class="pricing">
          <div class="prices">
            <div class="starting-price">
              <span>à partir de</span>
            </div>
            <div class="price">
              <span>{{ props.golf.best_price?.toFixed(2) }}€</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>
<style scoped>

.c-home-card {
  position: relative;
  z-index: 0;
  display: flex;
  width: 100%;
  flex: 0 0 auto;
  flex-direction: column;

  .picture {
    position: relative;
    flex: 0 0 auto;
    border-radius: 4px;
    aspect-ratio: 16 / 10;
    background-color: var(--color-secondary);
    background-position: center;
    background-size: cover;

    button {
      position: absolute;
      right: 1em;
      bottom: 1em;
      display: flex;
      width: 2.125em;
      height: 2.125em;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: none;
      border-radius: 50%;
      background-color: white;
      font-size: 1em;

      .c-icon {
        color: var(--color-secondary);
        opacity: 0.2;

        &.active {
          color: var(--color-primary);
          opacity: 1;
        }
      }
    }
  }

  .content {
    display: flex;
    width: 100%;
    flex: 1 1 auto;
    flex-direction: column;
    padding: 0.625em 0.875em;

    .golf-details {
      display: flex;
      min-width: 0;
      flex: 1 1 auto;
      flex-direction: column;
      justify-content: space-between;

      .name-promotion {
        display: flex;
        flex: 1 1 auto;
        flex-direction: row;
        justify-content: space-between;

        .name {
          overflow: hidden;
          max-width: 100%;
          text-overflow: ellipsis;
          white-space: nowrap;

          span {
            color: var(--color-secondary);
            font-family: Bariol, sans-serif;
            font-size: 1.25em;
            font-weight: bold;
          }
        }
      }

      .location {
        margin: 0.125em 0;
        opacity: 0.8;

        span {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 0.875em;
          font-weight: 600;
        }
      }

      .rating {
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        padding: 0.25em 0;
        margin: 0.125em 0;

        .c-icon {
          margin: 0 0 0.125em;
          color: var(--color-primary);
        }

        .note,
        .rates-count,
        .no-rate {
          margin: 0 0.25rem;
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 0.875rem;
          font-weight: 500;
          line-height: 1rem;
          opacity: 0.8;

          strong {
            color: var(--color-secondary);
            font-family: Bariol, sans-serif;
            font-size: 1.625rem;
            font-weight: 700;
          }

          &.no-rate {
            margin: 0;
          }
        }
      }
    }

    .bottom {
      display: flex;
      flex: 1 1 auto;
      flex-direction: row;
      align-items: flex-end;

      .golf-infos {
        display: flex;
        overflow: hidden;
        flex: 1 1 auto;
        flex-direction: column;
        margin-right: 0.25em;

        .c-holes {
          margin: 0.125em 0;
        }

        .services {
          display: flex;
          width: 100%;
          flex-direction: row;
          margin: 0.125em 0;
          opacity: 0.6;

          span {
            display: inline-block;
            overflow: hidden;
            width: 100%;
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 0.875em;
            font-weight: normal;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }

      .pricing {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .c-icon.promotion {
          justify-content: flex-end;
          color: var(--color-promo);
        }

        .prices {
          .starting-price {
            opacity: 0.6;
            white-space: nowrap;

            span {
              color: var(--color-secondary);
              font-family: Open-Sans, sans-serif;
              font-size: 0.75em;
              font-weight: normal;
            }
          }

          .price {
            span {
              color: var(--color-primary);
              font-family: Bariol, sans-serif;
              font-size: 2.25em;
              font-weight: bold;
              line-height: 2.25rem;
            }
          }
        }
      }
    }
  }

  &.in-paper {
    .content {
      padding: 0 1em 1em;
    }
  }
}
</style>
